import { FeaturedProduct } from "entities/HomePageBannerList";
import usePagination from "hooks/usePagination";
import React, { FC, useEffect, useId, useRef, useState } from "react";
import NcImage from "shared/NcImage/NcImage";
import ButtonCSCLed from "shared/Button/ButtonCSCLed";
import HeadingText1 from "components/HeadingText/HeadingText1";
import Glide from "@glidejs/glide";
import NextPrev from "shared/NextPrev/NextPrev";
import { Link } from "react-router-dom";

export interface FeaturedProductsProps {
  className?: string;
  productList: FeaturedProduct[];
  headerTitle?: string;
}

const FeaturedProducts: FC<FeaturedProductsProps> = ({
  className = "",
  productList,
  headerTitle,
}) => {
  const sliderRef = useRef(null);
  const id = useId();
  const UNIQUE_CLASS = "glidejs" + id.replace(/:/g, "_");
  const [visibleSlides, setVisibleSlides] = useState(3);

  useEffect(() => {
    if (!sliderRef.current) {
      return () => {};
    }

    // @ts-ignore
    const OPTIONS: Glide.Options = {
      perView: 3,
      gap: 22,
      bound: true,
      breakpoints: {
        1280: {
          perView: 4 - 1,
        },
        1024: {
          gap: 20,
          perView: 2.5,
        },
        768: {
          gap: 60,
          perView: 4 - 2,
        },
        640: {
          gap: 20,
          perView: 1.5,
        },
        500: {
          gap: 20,
          perView: 1.1,
        },
      },
    };

    let slider = new Glide(`.${UNIQUE_CLASS}`, OPTIONS);
    slider.mount();
    return () => {
      slider.destroy();
    };
  }, [sliderRef, UNIQUE_CLASS]);

  const updateResponsiveState = () => {
    const width = window.innerWidth;
    const tileSide = getPerViewByWidth(width);
    setVisibleSlides(tileSide);
  };

  const getPerViewByWidth = (width: number) => {
    if (width <= 500) return 1.1;
    if (width <= 640) return 1.5;
    if (width <= 768) return 2;
    if (width <= 1024) return 2.5;
    if (width <= 1280) return 3;
    return 3;
  };

  useEffect(() => {
    updateResponsiveState();
    window.addEventListener("resize", updateResponsiveState);
    return () => window.removeEventListener("resize", updateResponsiveState);
  }, []);

  const ProductCard = (product: FeaturedProduct) => {
    return (
      <>
        <div
          className={`nc-ProductCard flex flex-col ${className}`}
          data-nc-id="ProductCard"
        >
          <Link to={product.url} className="block">
            <NcImage
              containerClassName="h-64 md:h-96 w-full sm:w-96 overflow-hidden rounded-2xl hover:shadow-lg hover:shadow-slate-500"
              src={product.fileUrl}
              className="h-full w-full object-cover object-center drop-shadow-xl"
            />
          </Link>
          <div className="flex flex-col space-y-4 pt-5 pb-2.5 h-44">
            <div>
              <h2 className={`font-semibold text-lg sm:text-2xl text-csccolor`}>
                {product.title}
              </h2>
              <p className={`text-sm sm:text-base dark:text-slate-400 mt-1 `}>
                {product.subtitle}
              </p>
            </div>
          </div>
          <div className="mt-auto">
            <Link to={product.url}>
              <ButtonCSCLed className="">View Series</ButtonCSCLed>
            </Link>
          </div>
        </div>
      </>
    );
  };

  const HeaderSection = () => {
    return (
      <div className="nc-Section-Heading flex justify-center text-center w-full pb-10">
        <HeadingText1 className="text-csccolor font-poppins">
          {headerTitle}
        </HeadingText1>
      </div>
    );
  };

  return (
    <div className={`nc-FeaturedProducts container ${className} pt-10`}>
      <HeaderSection />
      <div className={`${UNIQUE_CLASS} flow-root`} ref={sliderRef}>
        <div className="glide__track" data-glide-el="track">
          <ul className="glide__slides">
            {productList.map((item, index) => (
              <li key={index} className="glide__slide">
                <ProductCard {...item} />
              </li>
            ))}
          </ul>
        </div>
        <div className="flex items-center justify-center mb-7">
          <NextPrev
            className={` ${
              productList.length > visibleSlides ? "visible" : "invisible"
            }`}
            onClickNext={() => {}}
            onClickPrev={() => {}}
          />
        </div>
      </div>
    </div>
  );
};

export default FeaturedProducts;
